/* =========================================================================
   "From guys who bought it" — the clips play

   Sixteen vertical cards in a scrolling rail. The problem with playing all
   of them is not playing: it is sixteen simultaneous downloads and sixteen
   decoders, which is what makes a rail like this stutter and then stick.

   So the rail plays a few at a time, and only the ones a shopper can see:

     near the screen   ->  the video is given its source
     on the screen     ->  it plays, muted and looping
     off the screen    ->  it pauses
     well away         ->  the source is taken back and the memory with it

   Never more than MAX_PLAYING at once, chosen by how much of each card is
   on screen, so scrolling fast does not leave a trail of decoders running
   behind it. Each card keeps its poster underneath the whole time, so the
   frame never changes size and nothing in the rail moves.

   Muted is not a preference here — an unmuted video cannot autoplay in any
   current browser. playsinline keeps iOS from taking the clip fullscreen.
   A shopper who has asked for less motion gets the posters and no playback
   at all.

   The section (sections/community-clips.liquid) renders the markup; this
   only ever sets src, plays and pauses. With this file absent the rail is
   the poster rail it always was.
   ========================================================================= */
(function () {
  'use strict';

  if (window.VroClipReels) return;

  /* Enough for a desktop row, which is about six cards wide — at four, two
     of the six a shopper was looking at sat frozen while the rest played.
     A phone never has more than three on screen, so this is a desktop
     number and costs a phone nothing. It is still a cap: the point of it
     is that a rail of sixteen never runs sixteen decoders. */
  var MAX_PLAYING = 6;

  /* Loaded a screen ahead so playback starts on arrival rather than a
     moment after it, and dropped two screens behind so a long rail does not
     keep every clip it has ever shown. */
  var LOAD_MARGIN = '100% 200px';
  var DROP_AFTER = 2;

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* A card with an empty data-vro-clip-src is one the section rendered
     ready for a reel it has not been given — the Instagram feed may fill it
     a moment from now, or never. Either way there is nothing to play yet, so
     it is not a video as far as this file is concerned. */
  function videos() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.community-clip-video[data-vro-clip-src]')
    ).filter(function (video) {
      return (video.getAttribute('data-vro-clip-src') || '').trim() !== '';
    });
  }

  function load(video) {
    var src = (video.getAttribute('data-vro-clip-src') || '').trim();
    if (!src) return;
    if (video.getAttribute('src') === src) return;
    video.setAttribute('src', src);
  }

  function unload(video) {
    if (!video.getAttribute('src')) return;
    try { video.pause(); } catch (e) { /* not started */ }
    video.classList.remove('is-vro-clip-ready');
    video.removeAttribute('src');
    /* Without this the browser keeps the buffer it already has, which is
       the whole reason for taking the source away. */
    try { video.load(); } catch (e) { /* jsdom, old Safari */ }
  }

  function play(video) {
    load(video);
    if (video.dataset.vroPlaying === '1') return;
    video.dataset.vroPlaying = '1';
    video.muted = true;
    video.loop = true;
    var attempt = video.play();
    /* Autoplay can still be refused — a data saver, a battery mode, a
       browser that wants a gesture first. The poster is underneath, so a
       refusal looks exactly like the rail did before and is not worth
       retrying into. */
    if (attempt && attempt.catch) {
      attempt.catch(function () { video.dataset.vroPlaying = ''; });
    }
  }

  function pause(video) {
    if (video.dataset.vroPlaying !== '1') return;
    video.dataset.vroPlaying = '';
    try { video.pause(); } catch (e) { /* not started */ }
  }

  /* How much of the card is on screen, as a fraction. Used to rank, so the
     clips that get to play are the ones most in front of the shopper. */
  function visibility(video) {
    var rect = video.getBoundingClientRect();
    var height = rect.height || 1;
    var top = Math.max(rect.top, 0);
    var bottom = Math.min(rect.bottom, window.innerHeight || 0);
    var vertical = Math.max(0, bottom - top) / height;

    /* A horizontal rail scrolls sideways, so a card can be fully within the
       viewport's height and still be off the side of it. */
    var width = rect.width || 1;
    var left = Math.max(rect.left, 0);
    var right = Math.min(rect.right, window.innerWidth || 0);
    var horizontal = Math.max(0, right - left) / width;

    return vertical * horizontal;
  }

  function settle() {
    if (reduced()) return;

    var seen = videos().map(function (video) {
      return { video: video, seen: visibility(video) };
    });

    /* Anything barely on screen is not a candidate: a sliver of a card at
       the edge of a rail is not something anybody is watching. */
    var candidates = seen.filter(function (entry) { return entry.seen > 0.35; })
      .sort(function (a, b) { return b.seen - a.seen; })
      .slice(0, MAX_PLAYING);

    var playing = candidates.map(function (entry) { return entry.video; });

    seen.forEach(function (entry) {
      if (playing.indexOf(entry.video) > -1) play(entry.video);
      else pause(entry.video);
    });
  }

  /* One frame per scroll, not one per scroll event. */
  var frame = null;
  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(function () { frame = null; settle(); });
  }

  function watchLoading() {
    if (!('IntersectionObserver' in window)) {
      /* No observer: give every clip its source and let settle() decide who
         plays. Older browsers, and the rail still works. */
      videos().forEach(load);
      return;
    }

    var near = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) load(entry.target);
      });
    }, { rootMargin: LOAD_MARGIN });

    var far = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) unload(entry.target);
      });
    }, { rootMargin: (DROP_AFTER * 100) + '% 400px' });

    videos().forEach(function (video) {
      if (video.dataset.vroClipObserved) return;
      video.dataset.vroClipObserved = '1';
      near.observe(video);
      far.observe(video);
    });
  }

  function bindVideos() {
    videos().forEach(function (video) {
      if (video.dataset.vroClipBound) return;
      video.dataset.vroClipBound = '1';
      video.loop = true;
      video.addEventListener('loadeddata', function () { video.classList.add('is-vro-clip-ready'); });
      video.addEventListener('playing', function () { video.classList.add('is-vro-clip-ready'); });
      video.addEventListener('ended', function () { video.currentTime = 0; play(video); });
    });
  }

  function closeModal(modal) {
    if (!modal) return;
    var video = modal.querySelector('[data-vro-clip-modal-video]');
    if (video) { try { video.pause(); } catch (e) {} video.removeAttribute('src'); }
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
  }

  function openModal(card) {
    var section = card.closest('.community-clips-section');
    var modal = section && section.querySelector('[data-vro-clip-modal]');
    var sourceVideo = card.querySelector('.community-clip-video');
    var source = sourceVideo && (sourceVideo.getAttribute('data-vro-clip-src') || sourceVideo.getAttribute('src'));
    if (!modal || !source) return false;
    var video = modal.querySelector('[data-vro-clip-modal-video]');
    if (!video) return false;
    video.src = source;
    video.muted = false;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    video.play().catch(function () { video.muted = true; video.play().catch(function () {}); });
    return true;
  }

  function bindModal() {
    if (document.documentElement.dataset.vroClipModalBound) return;
    document.documentElement.dataset.vroClipModalBound = '1';
    document.addEventListener('click', function (event) {
      var close = event.target.closest('[data-vro-clip-modal-close]');
      if (close) { closeModal(close.closest('[data-vro-clip-modal]')); return; }
      var card = event.target.closest('.community-clip-card');
      if (card && openModal(card)) event.preventDefault();
    });
  }

  /* Wiring the listeners is not the same question as having something to
     play, and it used to be asked as one: start() returned before any of
     this when the rail held no clips yet.

     Which is now always. Every card waits on the Instagram feed, so at
     DOMContentLoaded there is not a single source on the page, start()
     gave up, and nothing was ever listening. The feed arrived a moment
     later and called refresh(), which settled once — and the rail sits far
     down the home page, so nothing was on screen to settle on. Scrolling
     to it changed nothing, because nothing was watching the scroll. The
     rail stayed on its posters however far you scrolled.

     So this runs once, whether or not there is anything to play yet, and
     refresh() calls it too — a rail that fills later gets a listening page
     the same as one that arrived full. */
  var wired = false;
  function wire() {
    if (wired) return;
    wired = true;

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    /* The rail is its own scroller, so its scrolling is not the window's. */
    document.querySelectorAll('.community-clips-rail').forEach(function (rail) {
      rail.addEventListener('scroll', schedule, { passive: true });
    });

    /* A tab in the background should not be decoding video. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) videos().forEach(pause);
      else schedule();
    });
  }

  function start() {
    /* The modal is a click, not motion: it is bound whatever else is true
       of this page, including a rail that has nothing in it yet. */
    bindModal();

    /* Asked of the rail rather than of the clips in it: the rail is what
       says this page has one, and it has one before it has any. A page
       without one wires nothing. */
    if (!document.querySelector('.community-clips-rail')) return;

    bindVideos();

    if (reduced()) {
      /* Posters only. Nothing is fetched and nothing moves. */
      return;
    }

    wire();
    if (!videos().length) return;
    watchLoading();
    settle();
  }

  /* refresh() is for assets/vro-instagram-feed.js: it fills cards that had
     no reel, which changes what videos() answers, so the new ones have to be
     bound and observed — and the page has to be listening for the scroll
     that brings them into view. Cheap enough to call on every fill. */
  window.VroClipReels = {
    settle: settle,
    refresh: function () {
      bindModal();
      bindVideos();
      if (reduced()) return;
      wire();
      watchLoading();
      settle();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
